import { useCallback } from 'react';
import { ChartLegendRectangle } from '@/shared/components/ChartLegendRectangle';
import {
  type LineChartDatum,
  type LineChartVerticalLine,
  LineChart,
} from '@/shared/components/charts/LineChart';
import { apyToText } from '@/shared/utils/boost';
import { getNFoldTickValues } from '@/shared/utils/charts';
import { pipsToPercent } from '@/shared/utils/numbers';

const BORROW_RATE_COLOR = '#46DA93';
const UTILISATION_RATE_COLOR = '#FF33AF';

type AggregatedData = ReturnType<typeof aggregateData>;

type InterestRateModelData = {
  maxUtilisationBps?: number;
  junctionUtilisationBps: number;
  interestAtJunctionBps: number;
  interestAtZeroUtilisationBps: number;
  interestAtMaxUtilisationBps: number;
  utilisationRateBps: number;
};

type PointBps = { x: number; y: number };

const getInterestRateLinePoints = (
  p1: PointBps,
  p2: PointBps,
  p3: PointBps,
  maxUtilisationBps: number = 10000,
): LineChartDatum[] => {
  const slopeSegment1 = (p2.y - p1.y) / (p2.x - p1.x);
  const slopeSegment2 = (p3.y - p2.y) / (p3.x - p2.x);
  const points: LineChartDatum[] = [];

  for (let utilisationBps = 0; utilisationBps <= maxUtilisationBps; utilisationBps += 100) {
    let yBps: number;
    if (utilisationBps <= p2.x) {
      yBps = slopeSegment1 * (utilisationBps - p1.x) + p1.y;
    } else yBps = slopeSegment2 * (utilisationBps - p2.x) + p2.y;

    points.push({
      x: pipsToPercent(utilisationBps),
      y: pipsToPercent(yBps),
    });
  }
  return points;
};

const aggregateData = (prop: InterestRateModelData | undefined) => {
  let chartData: LineChartDatum[] = [];
  let verticalLines: LineChartVerticalLine[] = [];

  if (prop) {
    const {
      maxUtilisationBps = 10000,
      junctionUtilisationBps,
      interestAtJunctionBps,
      interestAtZeroUtilisationBps,
      interestAtMaxUtilisationBps,
      utilisationRateBps,
    } = prop;
    const p1 = { x: 0, y: interestAtZeroUtilisationBps };
    const p2 = { x: junctionUtilisationBps, y: interestAtJunctionBps };
    const p3 = { x: maxUtilisationBps, y: interestAtMaxUtilisationBps };
    chartData = getInterestRateLinePoints(p1, p2, p3, maxUtilisationBps);

    const maxY = pipsToPercent(interestAtMaxUtilisationBps);
    const optimalLineHeight = Math.max(20, (maxY * pipsToPercent(junctionUtilisationBps)) / 100);
    const currentLineHeight = Math.max(20, (maxY * pipsToPercent(utilisationRateBps)) / 100);

    verticalLines = [
      {
        x: pipsToPercent(utilisationRateBps),
        y1: 0,
        y2: currentLineHeight,
        label: 'Current',
      },
      {
        x: pipsToPercent(junctionUtilisationBps),
        y1: 0,
        y2: optimalLineHeight,
        label: 'Optimal',
      },
    ];
  }

  return { chartData, verticalLines };
};

const legendLabels = [
  { label: 'Borrow Rate, Current', color: BORROW_RATE_COLOR },
  { label: 'Utilisation Rate', color: UTILISATION_RATE_COLOR },
];

const Chart = ({ data, isLoading }: { data: AggregatedData; isLoading: boolean }) => {
  const maxX = data.chartData.length ? Math.max(...data.chartData.map((d) => Number(d.x))) : 100;
  const maxY = data.chartData.length ? Math.max(...data.chartData.map((d) => d.y)) : 0;

  const xTickValues = getNFoldTickValues(maxX, 25);
  const yTickValues = getNFoldTickValues(maxY, 10);

  const renderTooltip = useCallback(
    (datum: LineChartDatum) => (
      <div className="w-[220px] space-y-3">
        <div className="flex flex-col gap-y-2.5 text-12 leading-6 text-cf-white">
          <div>
            <div className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-x-1">
                <ChartLegendRectangle fill={BORROW_RATE_COLOR} />
                <div>Borrow Rate: </div>
              </div>
              <span className="font-aeonikMedium">{apyToText(datum.y)}</span>
            </div>
            <div className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-x-1">
                <ChartLegendRectangle fill={UTILISATION_RATE_COLOR} />
                <div>Utilisation Rate: </div>
              </div>
              <span className="font-aeonikMedium">{apyToText(Number(datum.x))}</span>
            </div>
          </div>
        </div>
      </div>
    ),
    [],
  );

  return (
    <LineChart
      isLoading={isLoading}
      data={data.chartData}
      color={BORROW_RATE_COLOR}
      xType="linear"
      curve="linear"
      margin={{ top: 20, right: 40, bottom: 50, left: 50 }}
      xTickValues={xTickValues}
      xTickFormat={(v) => `${v}%`}
      yTickValues={yTickValues}
      yTickFormat={(v) => `${v}%`}
      verticalLines={data.verticalLines}
      renderTooltip={renderTooltip}
    />
  );
};

export const interestRateModelChartProps = {
  Chart,
  aggregateData,
  legendLabels,
};
